/**
 * A conta que o operador declara (spec 007, decisao 21). A Prop nao consulta a Nelogica: o operador cria (ou move) a
 * conta na extranet e diz aqui a subconta e a mesa. A mesa sai da lista da etapa, com a sugerida ja marcada; a subconta
 * e digitada ou escolhida entre as contas que o sistema ja conhece da pessoa (a 296 que volta, decisao 7). O "serve" de
 * cada conta vem da MESMA regra que o backend usa ao aprovar. Antes de gravar, o operador confere tudo num dialogo.
 */
import { Aviso, Botao, Campo, Carregando, Dialogo, ErroApi, Fatos, ListaDeEscolha, Selo, formatarDia, soDigitos, type OpcaoDeEscolha } from "@mtc/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { ETAPA, STATUS_DA_MATRICULA, erroDe, useApi, type Etapa, type StatusMatricula } from "../api";

export type ContaDeclarada = { subconta: string; mesa: string };
type MesaDaEtapa = { conta: string; nome: string };
type ContaConhecida = { subconta: string; mesa: string; mesa_nome: string | null; etapa: Etapa | null; plano: string; status_da_matricula: StatusMatricula; desta_matricula: boolean; declarada_em: string | null; serve?: boolean; motivo?: string | null };
type Resposta = { etapa: Etapa | null; mesas: MesaDaEtapa[]; mesa_sugerida: string | null; itens: ContaConhecida[] };

export function useContasConhecidas(matriculaId: string, etapa: Etapa) {
  const { ler } = useApi();
  return useQuery({ queryKey: ["contas-conhecidas", matriculaId, etapa], queryFn: () => ler<Resposta>(`/v1/admin/matriculas/${matriculaId}/contas-conhecidas?etapa=${etapa}`) });
}

const chave = (c: ContaDeclarada) => `${c.subconta}@${c.mesa}`;
/** O par subconta e mesa como a confirmacao de "conta em uso" o identifica. */
export const parDaConta = (c: ContaDeclarada) => `${c.subconta.trim()}@${c.mesa}`;

/** O pedido de confirmacao do backend: a subconta ja esta com outro plano em andamento na mesma mesa (Vitor, 01/10). */
export const CONFIRMAR_CONTA_EM_USO = "CONFIRMAR_CONTA_EM_USO";
export type CorpoDaConfirmacao = { confirmar_conta_em_uso?: true };

/**
 * A conta declarada ja esta com outro plano em andamento na mesma mesa: o backend nao trava, pergunta (409
 * `CONFIRMAR_CONTA_EM_USO`). Isto guarda a frase dele pro par declarado e, depois de "Confirmar mesmo assim", a
 * confirmacao. Mudou a subconta ou a mesa, o aviso e a confirmacao somem: valiam pro par antigo.
 * `enviar` roda o pedido pela acao da tela e devolve se gravou; o pedido de confirmacao nao vira erro.
 */
export function useContaEmUso(par: string) {
  const [estado, setEstado] = useState<{ par: string; frase: string; confirmada: boolean } | null>(null);
  useEffect(() => setEstado((e) => (e && e.par !== par ? null : e)), [par]);
  const vale = estado?.par === par ? estado : null;
  const enviar = async (rodar: (f: () => Promise<unknown>) => Promise<boolean>, pedido: (extra: CorpoDaConfirmacao) => Promise<unknown>, confirmar = Boolean(vale?.confirmada)): Promise<boolean> => {
    let gravou = false;
    const ok = await rodar(async () => {
      try {
        await pedido(confirmar ? { confirmar_conta_em_uso: true } : {});
        gravou = true;
      } catch (err) {
        if (err instanceof ErroApi && err.status === 409 && err.corpo?.codigo === CONFIRMAR_CONTA_EM_USO) {
          setEstado({ par, frase: err.corpo.erro ?? "A subconta já está com outro plano em andamento nesta mesa.", confirmada: false });
          return;
        }
        throw err;
      }
    });
    return ok && gravou;
  };
  return {
    /** A frase do backend enquanto falta confirmar; nulo sem pergunta. */
    frase: vale && !vale.confirmada ? vale.frase : null,
    confirmada: Boolean(vale?.confirmada),
    enviar,
    /** "Confirmar mesmo assim": guarda a confirmacao do par e reenvia com ela. */
    confirmarEReenviar: (rodar: (f: () => Promise<unknown>) => Promise<boolean>, pedido: (extra: CorpoDaConfirmacao) => Promise<unknown>) => {
      setEstado((e) => (e ? { ...e, confirmada: true } : e));
      return enviar(rodar, pedido, true);
    },
  };
}

/**
 * `modo`: "nova" quando a conta foi criada na extranet (liberacao e Mesa Real): subconta e mesa; "mover" quando a mesma
 * conta mudou de mesa (Teste pro Simulador): so a mesa, e a subconta so se mudou.
 */
export function DeclararConta({ matriculaId, etapa, modo, subcontaAtual, valor, aoMudar }: { matriculaId: string; etapa: Etapa; modo: "nova" | "mover"; subcontaAtual?: string | null; valor: ContaDeclarada; aoMudar: (c: ContaDeclarada) => void }) {
  const q = useContasConhecidas(matriculaId, etapa);
  // A sugerida (primeira mesa da etapa sem conta viva da pessoa) ja vem marcada; o operador troca se criou em outra.
  useEffect(() => {
    if (!q.data || valor.mesa) return;
    const mesa = q.data.mesa_sugerida ?? (q.data.mesas.length === 1 ? q.data.mesas[0]!.conta : "");
    if (mesa) aoMudar({ ...valor, mesa });
  }, [q.data]); // so quando a lista chega

  if (q.isLoading) return <Carregando compacto texto="Buscando as mesas" />;
  if (q.isError || !q.data) return <Aviso tipo="erro">{erroDe(q.error)}</Aviso>;
  const { mesas, mesa_sugerida, itens } = q.data;
  const conhecidas = modo === "nova" ? itens : [];
  const mesasOp: OpcaoDeEscolha[] = mesas.map((m) => ({ valor: m.conta, titulo: m.nome, detalhe: <span className="mtc-mono">{m.conta}</span>, selo: m.conta === mesa_sugerida ? <Selo tom="acento">Sugerida</Selo> : undefined }));
  const contasOp: OpcaoDeEscolha[] = conhecidas.map((c) => ({
    valor: chave(c),
    titulo: <>Subconta <span className="mtc-mono">{c.subconta}</span></>,
    detalhe: [`${c.mesa_nome ?? "Mesa"} (${c.mesa})`, c.plano, `${STATUS_DA_MATRICULA[c.status_da_matricula]?.rotulo ?? c.status_da_matricula}${c.declarada_em ? ` desde ${formatarDia(c.declarada_em)}` : ""}`].join(". "),
    selo: c.desta_matricula ? <Selo>Atual</Selo> : c.serve ? <Selo tom="sucesso">Serve</Selo> : <Selo>Não serve</Selo>,
    desabilitada: !c.serve,
    motivo: c.motivo ?? undefined,
  }));
  return (
    <div className="mtc-pilha" style={{ gap: "var(--e-4)" }}>
      {!mesas.length ? (
        <Aviso tipo="erro">Nenhuma mesa ativa cadastrada para {ETAPA[etapa]}. Cadastre a mesa antes de decidir.</Aviso>
      ) : (
        <ListaDeEscolha
          rotulo={modo === "mover" ? `Mesa de ${ETAPA[etapa]} para onde a conta foi movida` : `Mesa de ${ETAPA[etapa]} em que a conta foi criada`}
          nome={`mesa-${matriculaId}-${etapa}`}
          opcoes={mesasOp}
          valor={valor.mesa}
          aoMudar={(mesa) => aoMudar({ ...valor, mesa })}
          dica="A sugerida é a primeira mesa da etapa em que a pessoa não tem conta de plano em andamento."
        />
      )}
      {modo === "nova" ? (
        <Campo rotulo={`Subconta que a extranet gerou${etapa === "MESA_REAL" ? " na Mesa Real" : ""}`} valor={valor.subconta} aoMudar={(v) => aoMudar({ ...valor, subconta: soDigitos(v) })} inputMode="numeric" mono placeholder="Só os dígitos" dica={subcontaAtual ? `Na Mesa Real a conta é outra: não é a atual (${subcontaAtual}).` : "Só os dígitos, como a extranet mostra."} />
      ) : (
        <Campo rotulo="Subconta (só se mudou)" valor={valor.subconta} aoMudar={(v) => aoMudar({ ...valor, subconta: soDigitos(v) })} inputMode="numeric" mono placeholder={subcontaAtual ?? ""} dica={subcontaAtual ? `Em branco, vale a atual (${subcontaAtual}): a conta muda de mesa, o número fica.` : "O plano ainda não tem subconta: informe a da extranet."} />
      )}
      {conhecidas.length > 0 && (
        <ListaDeEscolha
          rotulo="Contas que o sistema já conhece desta pessoa"
          nome={`conhecidas-${matriculaId}-${etapa}`}
          opcoes={contasOp}
          valor={valor.subconta && valor.mesa ? chave(valor) : ""}
          aoMudar={(v) => { const c = conhecidas.find((x) => chave(x) === v); if (c) aoMudar({ subconta: c.subconta, mesa: c.mesa }); }}
          dica="Reinscrição volta pra mesma conta (mesma subconta e mesa). Serve: a subconta não está em outro plano em andamento nesta mesa. O mesmo número em outra mesa é outra conta."
        />
      )}
    </div>
  );
}

/**
 * O passo de conferir antes de gravar. O sistema nao ve a extranet: o que o administrador declarar aqui vira a conta do
 * cliente (e a auditoria guarda quem declarou). `emUso`: a frase do backend quando a subconta ja esta com outro plano em
 * andamento na mesma mesa; vira um alerta (nao erro) e o botao passa a "Confirmar mesmo assim". `antes`: a conta de
 * agora, quando a conferencia e de uma troca (Gerenciar plano), pra dizer o que continua e o que muda.
 */
export function ConfirmarConta({ aberto, aoFechar, aoConfirmar, carregando, erro, titulo, botao, cpf, etapa, conta, mesas, extra, emUso, aoConfirmarEmUso, antes }: {
  aberto: boolean; aoFechar: () => void; aoConfirmar: () => void; carregando: boolean; erro?: string; titulo: string; botao: string;
  cpf?: string; etapa: Etapa; conta: ContaDeclarada; mesas: MesaDaEtapa[]; extra?: { rotulo: string; valor: string; detalhe?: string }[];
  emUso?: string | null; aoConfirmarEmUso?: () => void; antes?: { subconta: string | null; mesa: string | null; etapa?: string | null };
}) {
  const mesa = mesas.find((m) => m.conta === conta.mesa);
  const subconta = conta.subconta.trim() || antes?.subconta || "";
  const pergunta = Boolean(emUso && aoConfirmarEmUso);
  const rodape: ReactNode = (
    <>
      <Botao variante="fantasma" onClick={aoFechar}>Voltar e corrigir</Botao>
      {pergunta ? <Botao carregando={carregando} onClick={aoConfirmarEmUso}>Confirmar mesmo assim</Botao> : <Botao carregando={carregando} onClick={aoConfirmar}>{botao}</Botao>}
    </>
  );
  const comparado = (agora: string, era: string | null | undefined) => (antes === undefined ? undefined : !era ? "Nova" : era === agora ? "Continua a mesma" : `Antes: ${era}`);
  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} titulo={titulo} descricao={cpf ? "O sistema não consulta a Nelogica. Confira na extranet que a conta é deste CPF e está nesta mesa." : "O sistema não consulta a Nelogica. Confira na extranet que a conta está nesta mesa."} rodape={rodape}>
      <div className="mtc-pilha" style={{ gap: "var(--e-3)" }}>
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        {emUso && <Aviso tipo="alerta">{emUso}</Aviso>}
        <p style={{ margin: 0 }}>
          Vai gravar: {subconta ? <>subconta <strong className="mtc-mono">{subconta}</strong></> : "a subconta atual"} na mesa <strong className="mtc-mono">{conta.mesa}</strong>{mesa ? ` ${mesa.nome}` : ""} ({ETAPA[etapa]}).
        </p>
        <Fatos
          itens={[
            ...(cpf ? [{ rotulo: "CPF", valor: cpf, mono: true }] : []),
            { rotulo: "Subconta", valor: subconta || "A atual", mono: Boolean(subconta), detalhe: comparado(subconta, antes?.subconta) },
            { rotulo: "Mesa", valor: conta.mesa, detalhe: [mesa?.nome, comparado(conta.mesa, antes?.mesa)].filter(Boolean).join(". ") || undefined, mono: true },
            { rotulo: "Etapa", valor: ETAPA[etapa], detalhe: antes?.etapa !== undefined ? comparado(ETAPA[etapa], antes.etapa) : undefined },
            ...(extra ?? []),
          ]}
        />
      </div>
    </Dialogo>
  );
}
